/**
 * The wishlist — one child's, on the child's own terms.
 *
 * This is the screen the waiting room is really for. Adding something costs
 * nothing, asking starts a clock the child can read, and letting go is one tap
 * and is never treated as a failure. Nothing on this page says no, warns, or
 * frowns: the numbers are stated plainly and the child decides. That is the
 * same rule the Fuel impact preview follows (CLAUDE.md, "The impact preview
 * never editorialises") and it matters more here, because the whole feature
 * only works if a child trusts it enough to keep using it.
 *
 * The stat at the top is the point of the exercise. It is the child's own
 * evidence about their own impulses — not a lesson delivered to them — which is
 * why the numbers are theirs and the sentence has no adjective in it.
 */

import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import {
  CLOSED_STATUSES, OUTCOME_LABEL, WISH_BUCKETS, addWish, ask, formatDay, giveUp, saveFor,
  stopSavingFor, useWishes,
} from '../lib/wishes.js';
import { HOUSE_RULES_DEFAULT, KIND_HINT, KIND_LABEL } from '../lib/houseRulesDefault.js';
import { BUCKET_LABEL, EMPTY_BALANCE, useBank } from '../lib/bank.js';
import {
  WISH_KINDS, askForWish, isReady, monthKey, priceInPeriods, savingProgress, toyCapStatus,
  waitDaysFor, waitOutcomes,
} from '../domain/waiting.js';
import { allowanceAt } from '../domain/allowance.js';
import { centsFromDollars, formatCents } from '../domain/money.js';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "the 3rd" — a toy day said the way a family says it out loud. */
function ordinal(n) {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/**
 * What the date would actually be, asked of the domain rather than worked out
 * here. `askForWish` is pure, so running it on a copy is free, and it is the
 * same call the button will make — a preview that computed its own date would
 * eventually disagree with the date the child is then given, and the child is
 * the one told two different things.
 */
function previewReadyOn(wish, rule, spending, today) {
  try {
    return askForWish(wish, { askedOn: today, rule, spending }).readyOn ?? null;
  } catch {
    return null;
  }
}

/**
 * The price in the child's own units, said the way their allowance arrives.
 * "About 3 weeks of allowance" is only true if the allowance is weekly, and a
 * sentence that is true for two families in three is worse than no sentence.
 */
function periodsLine(count, cadence) {
  if (count == null) return null;
  if (cadence === 'monthly') return `About ${plural(count, 'month', 'months')} of allowance`;
  if (cadence === 'biweekly') return `About ${plural(count, 'allowance', 'allowances')}`;
  return `About ${plural(count, 'week', 'weeks')} of allowance`;
}

/** Adding something. It commits to nothing, and the copy does not pretend otherwise. */
function AddWish({ onSubmit, busy }) {
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState('other');
  const [bucket, setBucket] = useState('spend');
  const [note, setNote] = useState('');
  const amountCents = centsFromDollars(amount);

  async function submit(e) {
    e.preventDefault();
    const ok = await onSubmit({
      title: title.trim(), amountCents, kind, bucket, note: note.trim() || null,
    });
    if (ok !== false) {
      setTitle(''); setAmount(''); setNote(''); setKind('other'); setBucket('spend');
    }
  }

  return (
    <form className="stack tight" onSubmit={submit}>
      <div className="row">
        <label style={{ flex: '2 1 12rem' }}>
          What it is
          <input
            value={title} maxLength={80} required placeholder="Lego set"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label style={{ flex: '1 1 7rem' }}>
          Price in dollars
          <input
            type="number" inputMode="decimal" min="0" step="0.01" required placeholder="0.00"
            value={amount} onChange={(e) => setAmount(e.target.value)}
          />
        </label>
      </div>

      {/*
        What kind of thing it is. It is asked because the house treats these
        differently — a build kit and a toy are not the same purchase — and
        saying so up front is fairer than a rule appearing at the end. None of
        the six is the wrong answer and the buttons do not rank them.
      */}
      <div className="stack tight" role="group" aria-labelledby="wish-kind">
        <span className="eyebrow" id="wish-kind">What kind of thing</span>
        <div className="row">
          {WISH_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              className={kind === k ? 'primary' : ''}
              style={{ flex: '1 1 7rem' }}
              onClick={() => setKind(k)}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        {KIND_HINT[kind] && <p className="small muted">{KIND_HINT[kind]}</p>}
      </div>

      <div className="row">
        <label style={{ flex: '1 1 8rem' }}>
          Out of
          <select value={bucket} onChange={(e) => setBucket(e.target.value)}>
            {WISH_BUCKETS.map((b) => (
              <option key={b} value={b}>{BUCKET_LABEL[b]}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: '2 1 12rem' }}>
          A note (optional)
          <input
            value={note} maxLength={120} placeholder="The one with the red doors"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>
      <button className="primary" type="submit" disabled={busy || !title.trim() || amountCents <= 0}>
        {busy ? 'Adding…' : 'Add it to the list'}
      </button>
    </form>
  );
}

/**
 * The bar under the saving line. Decoration only — the sentence above it
 * carries every number, so a child who cannot see it has lost nothing
 * (CLAUDE.md: a picture is never the only carrier of a fact).
 */
function SaveBar({ pct }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height: '.35rem',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--gauge-track)',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--gauge-fill)' }} />
    </div>
  );
}

function WishCard({
  wish, rule, spending, today, balanceCents, saveBalanceCents, allowanceCents, cadence,
  toyStatus, busy, onAsk, onLetGo, onSaveFor, onStopSaving,
}) {
  const periods = periodsLine(priceInPeriods(wish.amountCents, allowanceCents), cadence);
  const waitDays = waitDaysFor(wish.amountCents, rule);
  const ready = isReady(wish, today);
  const bucketName = BUCKET_LABEL[wish.bucket] ?? BUCKET_LABEL.spend;
  const isToy = wish.kind === 'toy';
  const saving = wish.status === 'saving';
  const progress = saving ? savingProgress({ wish, saveBalanceCents }) : null;

  // Only for a toy with a toy day, and only where asking is still ahead: the
  // date is the useful half of the rule, and stating it beats making a child
  // work out which day comes next.
  const wouldBeReady = isToy && spending.toyDay != null && (wish.status === 'wished' || saving)
    ? previewReadyOn(wish, rule, spending, today)
    : null;

  return (
    <div className="card stack tight">
      <div className="spread">
        <strong>{wish.title}</strong>
        <span className="money">{formatCents(wish.amountCents)}</span>
      </div>
      <p className="small muted">{KIND_LABEL[wish.kind] ?? KIND_LABEL.other}</p>
      {wish.note && <p className="small muted">{wish.note}</p>}

      {/* Their units, then their balance. Both plain facts, neither of them an
          argument: a child who reads the numbers and still wants the thing is
          allowed to ask, and that is the deal. */}
      {periods && <p className="small muted">{periods}</p>}
      <p className="small muted">
        You have <span className="money">{formatCents(balanceCents)}</span> in {bucketName}
      </p>

      {/* The toy rules, stated. Neither line is a warning and neither of them
          says no — a cap is a number and a toy day is a date, and a child who
          knows both can plan around them instead of being surprised by them. */}
      {isToy && toyStatus?.capCents != null && (
        <p className="small muted">
          Toys this month: <span className="money">{formatCents(toyStatus.spentCents)}</span> of{' '}
          <span className="money">{formatCents(toyStatus.capCents)}</span>
        </p>
      )}
      {isToy && spending.toyDay != null && (
        <p className="small muted">Toy day is the {ordinal(spending.toyDay)}</p>
      )}

      {wish.status === 'wished' && (
        <>
          <p className="small muted">
            {waitDays > 0
              ? `Asking starts a ${plural(waitDays, 'day', 'days')} wait`
              : 'No wait for this one'}
          </p>
          {wouldBeReady && (
            <p className="small muted">Asking now, it would be ready on {formatDay(wouldBeReady)}.</p>
          )}
          <div className="row">
            <button className="primary" disabled={busy} onClick={() => onAsk(wish)}>Ask</button>
            <button disabled={busy} onClick={() => onSaveFor(wish)}>Save up for it</button>
            <button className="quiet" disabled={busy} onClick={() => onLetGo(wish)}>Let it go</button>
          </div>
        </>
      )}

      {saving && progress && (
        <>
          <p className="small muted">
            <span className="money">{formatCents(progress.haveCents)}</span> of{' '}
            <span className="money">{formatCents(progress.targetCents)}</span> saved
            {progress.remainingCents > 0 && (
              <>, <span className="money">{formatCents(progress.remainingCents)}</span> to go</>
            )}
          </p>
          {/* The domain's own percentage, which floors — a bar that reads full
              at $19.99 of $20 is the same small betrayal as gold rounding up. */}
          <SaveBar pct={progress.pct} />
          {/* Asking early is allowed and nothing here discourages it. What it
              would mean is stated once, as a fact. */}
          {!progress.reached && (
            <p className="small muted">
              Your Save has <span className="money">{formatCents(saveBalanceCents)}</span>; the rest
              would be fronted.
            </p>
          )}
          {wouldBeReady && (
            <p className="small muted">Asking now, it would be ready on {formatDay(wouldBeReady)}.</p>
          )}
          <div className="row">
            <button className="primary" disabled={busy} onClick={() => onAsk(wish)}>Ask</button>
            <button className="quiet" disabled={busy} onClick={() => onStopSaving(wish)}>
              Stop saving for this
            </button>
          </div>
        </>
      )}

      {wish.status === 'waiting' && (
        <>
          <p className="small muted">
            {ready ? 'Ready — waiting for a grown-up' : `Ready on ${formatDay(wish.readyOn)}`}
          </p>
          {/* A waiver is loud or it doesn't happen. The child sees it too. */}
          {wish.waivedAt && <p className="small muted">A grown-up skipped the wait on this one.</p>}
          <div className="row">
            <button className="quiet" disabled={busy} onClick={() => onLetGo(wish)}>Let it go</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Wishlist() {
  const { childId } = useParams();
  const { family, kids, profile } = useFamily();
  const wishes = useWishes(family?.id);
  const bank = useBank(family?.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A child only ever gets their own list, whatever the URL says.
  if (profile && profile.id !== childId) return <Navigate to={`/wishes/${profile.id}`} replace />;

  const child = kids.find((k) => k.id === childId);
  if (!wishes.loading && !child) return <Navigate to={profile ? '/' : '/wishes'} replace />;

  const mine = wishes.byChild[childId] ?? [];
  const open = mine.filter(
    (w) => w.status === 'wished' || w.status === 'saving' || w.status === 'waiting',
  );
  const done = mine.filter((w) => CLOSED_STATUSES.includes(w.status));
  const outcomes = waitOutcomes(mine);

  const balance = bank.byChild[childId]?.balance ?? EMPTY_BALANCE;

  // What the job will actually pay them, priced from birth month and year at
  // today's date — the same number `ChildBank` quotes, for the same reason.
  const allowance = bank.allowanceRules[childId] ?? null;
  const allowanceCents = allowance && child
    ? allowanceAt(bank.today, { birthMonth: child.birth_month, birthYear: child.birth_year }, allowance).baseCents
    : 0;

  // A family that hasn't written their own words gets the starter ones rather
  // than an empty space. The sentence is what a child reads before they ask,
  // and no sentence at all is the one outcome that helps nobody.
  const houseRulesText = family?.house_rules ?? HOUSE_RULES_DEFAULT;
  const houseRules = String(houseRulesText).split(/\n+/).map((p) => p.trim()).filter(Boolean);

  // This child's toys this month, for the fact on a toy card. Read without a
  // candidate wish: it is what has already been spent, not what a purchase
  // would make it.
  const toyStatus = toyCapStatus(mine, {
    childId, month: monthKey(wishes.today), spending: wishes.spending,
  });

  async function run(fn) {
    setBusy(true); setError(null);
    try {
      await fn();
      await wishes.reload();
    } catch (e) {
      setError(`That didn’t save — ${e.message}.`);
      setBusy(false);
      return false;
    }
    setBusy(false);
    return true;
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        {profile
          ? <p className="eyebrow">{family.name}</p>
          : <BackLink to="/wishes">Wishes</BackLink>}
        <h1>{profile ? 'Your list' : `${child?.display_name ?? 'The'}’s list`}</h1>
      </div>

      {wishes.error && <p className="notice error">{wishes.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}
      {wishes.loading && <p className="muted" role="status">Loading…</p>}

      {/*
        The stat that does the teaching, and the reason the outcomes are two
        separate numbers in the domain: "the other N you kept" counts only the
        ones this child let go of. A parent's no is not the child's restraint,
        and counting it here would be flattery rather than evidence.
      */}
      {outcomes.waited > 0 && (
        <div className="card stack tight">
          <p className="eyebrow">Waiting</p>
          <p>
            You’ve waited on {plural(outcomes.waited, 'thing', 'things')}. You still
            wanted {outcomes.stillWanted}.
            {outcomes.letGo > 0 && (
              <> The other {outcomes.letGo} were <span className="money">{formatCents(outcomes.keptCents)}</span> you kept.</>
            )}
          </p>
        </div>
      )}

      {/*
        The family's own words, not the app's. This is the sentence a child
        reads before they ask, and it works because a parent wrote it.
      */}
      {houseRules.length > 0 && (
        <section className="card stack tight" aria-labelledby="house-rules">
          <p className="eyebrow" id="house-rules">How we spend</p>
          {houseRules.map((p, i) => <p key={i}>{p}</p>)}
        </section>
      )}

      <section className="card stack tight" aria-labelledby="add-wish">
        <p className="eyebrow" id="add-wish">Add something</p>
        <AddWish
          busy={busy}
          onSubmit={(values) => run(() => addWish({ familyId: family.id, childId, ...values }))}
        />
      </section>

      {open.map((wish) => (
        <WishCard
          key={wish.id}
          wish={wish}
          rule={wishes.rule}
          spending={wishes.spending}
          today={wishes.today}
          balanceCents={balance[wish.bucket] ?? 0}
          saveBalanceCents={balance.save ?? 0}
          allowanceCents={allowanceCents}
          cadence={allowance?.cadence ?? 'weekly'}
          toyStatus={toyStatus}
          busy={busy}
          onAsk={(w) => run(() => ask({
            wish: w, rule: wishes.rule, spending: wishes.spending, today: wishes.today,
          }))}
          onSaveFor={(w) => run(() => saveFor({ wish: w, today: wishes.today }))}
          onStopSaving={(w) => run(() => stopSavingFor({ wish: w, today: wishes.today }))}
          onLetGo={(w) => run(() => giveUp({ wish: w, today: wishes.today }))}
        />
      ))}

      {!wishes.loading && open.length === 0 && (
        <p className="muted">Nothing on the list yet.</p>
      )}

      {done.length > 0 && (
        <details className="card">
          <summary>Done ({done.length})</summary>
          <ul
            className="stack tight"
            style={{ listStyle: 'none', padding: 0, margin: 'var(--space-3) 0 0' }}
          >
            {done.map((w) => (
              <li key={w.id} className="spread small">
                <span>
                  {w.title} <span className="muted">· {OUTCOME_LABEL[w.status]}</span>
                </span>
                <span className="money">{formatCents(w.amountCents)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </main>
  );
}
